import { FormBlock, Toggle } from '@qbe/ads-blocks-forms';
import { Box, FormGroupLabel, ToggleProps, Typography } from '@qbe/ads-core';
import * as React from 'react';

import { UnorderedList, UnorderedListItem } from '~/components-ds/UnorderedList';
import { ANZSIC_TO_CPI_TYPES } from '~/constants/domain-specific';
import { simpleQuestionOptionsBoolean } from '~/constants/questions';
import { useAppContent, useAppDispatch, useCoverDetails } from '~/hooks';
import { updateCpiQLD, updateCpiVIC } from '~/slices/coverDetails';
import { AnzsicCPIType } from '~/types/cover';

import { ContextualHelpWithButton } from '../../components-ds/ContextualHelp/ContextualHelpWithButton';
import { PlumbingLicences } from './PlumbingLicences';

interface ConsumerProtectionInsuranceProps {
  anzicCode: string;
}

const ConsumerProtectionInsurance: React.FC<ConsumerProtectionInsuranceProps> = ({ anzicCode }) => {
  const { getTextContent, getJSXContent } = useAppContent({
    basePath: 'dsme.coverDetailsBusinessLiability',
  });

  const dispatch = useAppDispatch();
  const { cpiQLD, cpiVIC } = useCoverDetails();

  const occupationType = ANZSIC_TO_CPI_TYPES[anzicCode];

  const isElectrician = occupationType.includes(AnzsicCPIType.ElectricianQLD);
  const isPlumber = occupationType.includes(AnzsicCPIType.PlumberVIC);

  return (
    <Box data-track="cpi" data-track-value="capture">
      <FormGroupLabel
        title={getTextContent('fields.selectCpi.title')}
        alignCenter
        titleSuffix={
          <ContextualHelpWithButton tracking="help: Do you need consumer protection insurance?">
            {isElectrician && (
              <Typography>
                {getJSXContent('contextualhelpers.cpiElectrician.description')}
              </Typography>
            )}
            {isPlumber && (
              <Typography>{getJSXContent('contextualhelpers.cpiPlumber.description')}</Typography>
            )}
          </ContextualHelpWithButton>
        }
      />
      {isElectrician && (
        <>
          <Typography textAlign="center">
            {getTextContent('fields.selectCpi.descriptionElectrician')}
          </Typography>
          <UnorderedList sx={{ listStyleType: 'none' }}>
            <UnorderedListItem sx={{ textAlign: 'center', fontWeight: 'bold' }}>
              Queensland
            </UnorderedListItem>
          </UnorderedList>

          <FormBlock preset="toggle" paddingTop="none">
            <Toggle
              title="" // TODO: https://qbe-appservices.atlassian.net/browse/AD1-405
              titleHidden
              name="cpiQLD"
              optionList={simpleQuestionOptionsBoolean.map(
                ({ text, value: v }): ToggleProps['optionList'][number] => ({
                  id: `cpiQLD_${text}`,
                  label: text,
                  value: v.toString(),
                  defaultChecked: cpiQLD === v,
                  onChange: (e) => dispatch(updateCpiQLD(JSON.parse(e.target.value))),
                })
              )}
            />
          </FormBlock>
        </>
      )}
      {isPlumber && (
        <>
          <Typography textAlign="center">
            {getTextContent('fields.selectCpi.descriptionPlumber')}
          </Typography>
          <UnorderedList sx={{ listStyleType: 'none' }}>
            <UnorderedListItem sx={{ textAlign: 'center', fontWeight: 'bold' }}>
              Victoria
            </UnorderedListItem>
          </UnorderedList>

          <FormBlock preset="toggle" paddingTop="none">
            <Toggle
              title=""
              titleHidden
              name="cpiVIC"
              optionList={simpleQuestionOptionsBoolean.map(
                ({ text, value: v }): ToggleProps['optionList'][number] => ({
                  id: `cpiVIC_${text}`,
                  label: text,
                  value: v.toString(),
                  defaultChecked: cpiVIC === v,
                  onChange: (e) => dispatch(updateCpiVIC(JSON.parse(e.target.value))),
                })
              )}
            />
          </FormBlock>
        </>
      )}
      {cpiVIC && <PlumbingLicences />}
    </Box>
  );
};
export default ConsumerProtectionInsurance;
